/**
 * web/src/screens/Notebook/GiftingAgent.tsx — the gifting agent strip
 * (cards JKXJy2U9FVwx / wYPNXGVzAb63 / Mk8sHH7lfFDM / jqPcHtCfaE7K and
 * Gift Concierge V1, card Yw5UFURMXSTY).
 *
 * Rendered on the Notebook Gift tab. Card vC4xl3s56D4n splits the strip into
 * two clearly titled recipient sections, because the two halves had started
 * to blur together. Each card inside names who it is for.
 *
 *  FOR YOU — "For you — what would you like?"
 *   1. preference — the FOR-me answer the agent winnows by
 *
 *  FOR THEM — "For them — what would they like?"
 *   2. budget + ships-fast — one-tap HARD filters on the shopping pass
 *   3. "Find a gift" — the anytime entry (card TIL4cDJtZqfW)
 *   4. occasions — upcoming, each with a Suggest once it enters lead time
 *   5. "Just for them" — the read-only has_owned / not_taste rows (card
 *      QltAysLlIuwm: no manual add-form — capture now happens on the pick)
 *   6. recommendations — the flick-through stack when a search filled one,
 *      the single pick otherwise; Accept / Re-steer underneath, plus the
 *      shown-pick capture row (Already have it / Not their taste / Like it)
 *   7. the T+5 reaction — shown only once a pick is accepted and five days
 *      are up. A gift already given to the partner, so it stays in FOR THEM.
 *
 * Gated on the existing `vault` AI toggle (card Hsk5cyJ8ap1R).
 */
import { useCallback, useEffect, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { api, session } from '../../shell/bridge';
import { gracefulFailure } from '../../lib/errorCopy';
import { ConciergeOnButton, humanError } from './ConciergeOnButton';

interface GiftPreference {
  preference: 'experience' | 'clothing' | 'meal' | 'anything' | null;
}
interface GiftOccasion {
  key: string;
  label: string;
  date: string;
  leadDays: number;
  forMemberId: string | null;
  due: boolean;
}
interface GiftPick {
  title: string;
  price: string | null;
  url: string;
  rating: string | null;
  image: string | null;
  badge: string | null;
  availability: string | null;
  /** Card ggTQe2KbB0A3 MEDIUM-1 — this pick's own explanation. Absent on
   *  stacks stored before the field existed; the card then falls back to
   *  the recommendation-level rationale (the old single-sentence behaviour). */
  why?: string | null;
}
/** One notebook gift row the Results lane can show (card DqdHN32CEqTg). The
 *  server only ever lists rows the viewer may see, so a partner's 'just me'
 *  idea never reaches this client at all. */
interface GiftWishRow {
  itemId: string;
  entryType: string;
  subtype?: string | null;
  body: string | null;
  title: string | null;
  addedBy: string;
  shared: boolean;
}
interface GiftRecommendation {
  recId: string;
  occasionKey: string;
  occasionLabel: string;
  forMemberId: string | null;
  rationale: string;
  category: string;
  giftId: string | null;
  giftTitle: string | null;
  giftUrl: string | null;
  status: 'pending' | 'accepted' | 're-nested';
  stack: GiftPick[];
  orderBy: string | null;
  feedback: string | null;
  feedbackDue: boolean;
  pickFeedback: 'has_owned' | 'not_taste' | 'loved' | null;
  /** Card ggTQe2KbB0A3 MEDIUM-2 — answers keyed by lowercased pick title, so
   *  only the pick actually judged collapses to "Noted". */
  pickFeedbacks?: Record<string, 'has_owned' | 'not_taste' | 'loved'>;
  /** The SERVER's verdict on who may see this pick (card ggTQe2KbB0A3 HIGH-4):
   *  Results reads it rather than the local audience switch, so the line under
   *  a card can never claim "partner too" about a pick the server kept private. */
  isPrivate?: boolean;
  createdAt: number;
}
interface GiftPrivateRow {
  rowId: string;
  kind: 'has_owned' | 'not_taste' | 'loved';
  label: string;
  createdAt: number;
}

const PREFS: { value: 'experience' | 'clothing' | 'meal' | 'anything'; label: string }[] = [
  { value: 'experience', label: 'Experience' },
  { value: 'clothing', label: 'Clothing' },
  { value: 'meal', label: 'Meal' },
  { value: 'anything', label: 'Anything' },
];

/** Budget ceiling on the shopping pass (card suGPO1AGmKHj): free text, not
 *  presets. The typed maximum is validated here, saved to the member's
 *  preference record, and sent on Suggest — the server re-parses it, and a
 *  value it cannot read falls back to "no ceiling" rather than excluding.
 *  `ok: false` means the text is not an amount (blank is fine: no ceiling). */
function parseBudgetInput(text: string): { ok: boolean; value: number | null } {
  const cleaned = text.replace(/[$,\s]/g, '');
  if (!cleaned) return { ok: true, value: null };
  if (!/^\d+(\.\d+)?$/.test(cleaned)) return { ok: false, value: null };
  const n = Number(cleaned);
  return Number.isFinite(n) && n > 0 ? { ok: true, value: n } : { ok: false, value: null };
}

/** The two editable private kinds. `loved` rows are written by the T+5
 *  reaction, never typed — so there is no composer for it here. */
const PRIVATE_KINDS: { value: 'has_owned' | 'not_taste'; label: string; hint: string }[] = [
  { value: 'has_owned', label: 'Already has', hint: 'Never suggest these — they already own one.' },
  { value: 'not_taste', label: 'Not their taste', hint: 'Skipped silently. They will never see this list.' },
];

const REACTIONS: { value: 'loved' | 'not_quite' | 'not_gifted'; label: string }[] = [
  { value: 'loved', label: 'Loved it' },
  { value: 'not_quite', label: 'Not quite' },
  { value: 'not_gifted', label: 'Didn’t happen' },
];

/** Card QltAysLlIuwm — the shown-pick capture. Asking "already has / not
 *  their taste" BEFORE a recommendation is the burden the founder named, so
 *  the answer is asked ON the pick instead. Each tap files the matching
 *  private profile row; the concierge drops that title from the next pass. */
const PICK_VERDICTS: { value: 'has_owned' | 'not_taste' | 'loved'; label: string }[] = [
  { value: 'has_owned', label: 'Already have it' },
  { value: 'not_taste', label: 'Not their taste' },
  { value: 'loved', label: 'Like it' },
];

export function GiftingAgent() {
  const [preference, setPreference] = useState<'experience' | 'clothing' | 'meal' | 'anything' | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [occasions, setOccasions] = useState<GiftOccasion[]>([]);
  const [recs, setRecs] = useState<GiftRecommendation[]>([]);
  const [privateRows, setPrivateRows] = useState<GiftPrivateRow[]>([]);
  const [agentOn, setAgentOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Free-text maximum (card suGPO1AGmKHj): the raw text the member typed,
  // plus the validated value that travels on the wire. They diverge only
  // while the text is not an amount — and then nothing is sent or saved.
  const [budgetText, setBudgetText] = useState('');
  const [budgetMax, setBudgetMax] = useState<number | null>(null);
  const [budgetErr, setBudgetErr] = useState<string | null>(null);
  const [shipsFast, setShipsFast] = useState(false);
  const [stackIdx, setStackIdx] = useState<Record<string, number>>({});
  // Card ZCEkFGirrAOl — the three screens: Home = the occasion/budget/fast-ship
  // trigger bar + Find Gift Ideas, Brief = positive profile input only,
  // Results = the stack plus the collapsed Fine-Tune footer. `null` means the
  // member has not chosen, so the first paint lands where the work is: picks
  // waiting (or a T+5 due) -> results, otherwise home.
  const [step, setStep] = useState<'home' | 'brief' | 'results' | null>(null);
  // Card vm9sDf6VWc7S — one audience control on Brief. The privacy default is
  // the same one the gift save took in aDlJX4X7txMI: a pick stays invisible to
  // the partner unless the searcher says they may see it.
  const [audience, setAudience] = useState<'shared' | 'secret'>('secret');
  /* Desktop uplift A4 (card mPas6koLNVRr): at the shipped >=1100px token the
     Results screen becomes a two-column list + detail rail — brief left,
     the selected recommendation's detail right. `wide` is the same live seam
     A3 applies to Dates, so a resize across the token retires or restores the
     split instead of leaving a rail stranded beside a stack. Below the token
     `split` is false and the phone renders exactly the stack it shipped with. */
  const [wide, setWide] = useState(false);
  /* Which recommendation the rail is showing. null -> the first pending one,
     so the rail is never an empty box while there is anything to show. */
  const [selRec, setSelRec] = useState<string | null>(null);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1100px)');
    const sync = () => setWide(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  // Card TIL4cDJtZqfW — the anytime entry gifts the PARTNER, so it needs the
  // other member's id. Read once from the room (the same source the occasion
  // rows carry it from); no partner in the room yet -> null, i.e. joint.
  // Card vC4xl3s56D4n keeps the whole member list so every card can NAME its
  // recipient rather than leaving the two of them blurred together.
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [members, setMembers] = useState<{ memberId: string; displayName?: string }[]>([]);
  // Card DqdHN32CEqTg — the partner's own saved wishlist, read from the
  // notebook rows they made visible (the server never lists a partner's
  // 'just me' row, so privacy is enforced before this state exists).
  const [theirList, setTheirList] = useState<GiftWishRow[]>([]);
  useEffect(() => {
    const me = session()?.memberId ?? null;
    api<{ couple?: { members?: { memberId: string; displayName?: string }[] } }>('/api/pair/status')
      .then((d) => {
        const ms = d.couple?.members ?? [];
        setMembers(ms);
        setPartnerId(ms.find((m) => m.memberId && m.memberId !== me)?.memberId ?? null);
      })
      .catch(() => setPartnerId(null));
  }, []);

  /** Card vC4xl3s56D4n — who a card is FOR, in words. `you` when the row is
   *  the reader's own, the partner's display name when the room has one, and
   *  the plain "your partner" before any name is set — never a bare id. */
  const recipientName = (forMemberId: string | null): string => {
    const me = session()?.memberId ?? null;
    if (forMemberId && forMemberId === me) return 'you';
    const m = forMemberId ? members.find((x) => x.memberId === forMemberId) : null;
    const name = m?.displayName?.trim();
    return name ? name : 'your partner';
  };

  /** Card ggTQe2KbB0A3 HIGH-4 — who may see this pick, in words, read off the
   *  recommendation the SERVER returned (its `isPrivate`), never off the local
   *  audience switch. A recipient line alone let a card read "For you" over a
   *  pick the partner could not see, or the reverse. */
  const visibilityName = (r: Pick<GiftRecommendation, 'isPrivate'>): string =>
    r.isPrivate ? 'Only you can see this' : 'Your partner can see this too';

  const reload = useCallback(async () => {
    setErr(null);
    try {
      const [prefRes, occRes, recRes, privRes] = await Promise.all([
        api<{ preference: GiftPreference['preference']; budgetMax: number | null }>('/api/gifts/preference'),
        api<{ occasions: GiftOccasion[] }>('/api/gifts/occasions'),
        api<{ recommendations: GiftRecommendation[] }>('/api/gifts/recommendations'),
        api<{ rows: GiftPrivateRow[] }>('/api/gifts/private'),
      ]);
      setPreference(prefRes.preference);
      setBudgetMax(prefRes.budgetMax ?? null);
      setBudgetText(prefRes.budgetMax === null || prefRes.budgetMax === undefined ? '' : String(prefRes.budgetMax));
      setOccasions(occRes.occasions);
      setRecs(recRes.recommendations);
      setPrivateRows(privRes.rows);
      // Card DqdHN32CEqTg — one notebook read feeds the "On their list"
      // lane. listNotebook already drops the partner's private rows, so a
      // 'just me' wish can never render here; the addedBy filter keeps the
      // lane THEIRS. Kept out of the Promise.all: a notebook hiccup must
      // never take down the strip itself.
      api<{ items: GiftWishRow[] }>('/api/notebook')
        .then((n) => setTheirList(n.items ?? []))
        .catch(() => undefined);
    } catch (e) {
      setErr(humanError(e, 'Could not load the gifting concierge'));
    }
    // Card cEuitTMwLs6f — this used to force `setAgentOn(true)` on every
    // reload, which made the off card above UNREACHABLE: with the vault
    // permission off the panel rendered anyway, silently empty (the stored
    // recommendation list is read ungated), so the member who had already
    // unlocked the concierge saw a headline feature with nothing in it and no
    // way to turn it on. The toggle fetch owns `agentOn`; reload() does not.
    setLoaded(true);
  }, []);

  useEffect(() => {
    let live = true;
    api<{ toggles?: Record<string, boolean> }>('/api/ai/toggles')
      .then((d) => {
        if (!live) return;
        setAgentOn(d.toggles?.vault === true);
        return reload();
      })
      .catch(() => {
        if (live) setAgentOn(false);
      });
    return () => {
      live = false;
    };
  }, [reload]);

  const pickPreference = async (p: 'experience' | 'clothing' | 'meal' | 'anything') => {
    setErr(null);
    try {
      const res = await api<GiftPreference>('/api/gifts/preference', { method: 'PUT', body: { preference: p } });
      setPreference(res.preference);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save preference');
    }
  };

  /** Card suGPO1AGmKHj — the free-text maximum. Save on blur: the text is
   *  validated here before it travels, and an amount that does not parse is
   *  refused with a hint instead of being quietly dropped. Blank clears the
   *  ceiling. */
  const saveBudgetMax = async (text: string) => {
    const parsed = parseBudgetInput(text);
    if (!parsed.ok) {
      setBudgetErr('Enter an amount, like 80 — or leave it blank for any price.');
      return;
    }
    setBudgetErr(null);
    if (parsed.value === budgetMax) return; // unchanged — no write
    try {
      const res = await api<{ preference: GiftPreference['preference']; budgetMax: number | null }>(
        '/api/gifts/preference', { method: 'PUT', body: { budgetMax: parsed.value } },
      );
      setBudgetMax(res.budgetMax ?? null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save your maximum');
    }
  };

  const suggest = async (o: GiftOccasion) => {
    setBusy(true);
    setErr(null);
    try {
      const resp = await api<{
        recommendation?: { recId?: string };
        enrich?: { ok?: boolean; code?: string; engine?: string };
      }>('/api/gifts/recommendations', {
        method: 'POST',
        body: {
          occasionKey: o.key,
          occasionLabel: o.label,
          forMemberId: o.forMemberId,
          budgetMax,
          shipsFast,
          // Card vm9sDf6VWc7S — the Brief step's audience choice travels with
          // the request; the server stamps private_rec from it.
          audience,
        },
      });
      // Card ZCEkFGirrAOl — asking for a pick lands you on the pick.
      setStep('results');
      // Card 3CUjePam8ytj — the shopping enrich tool can be out of credit /
      // unkeyed / down, and that used to land as a bare single-pick card with
      // no explanation. When the server says the enrich failed for a service
      // reason (not no_results), apologise and name the code instead.
      if (resp.enrich && resp.enrich.ok === false) {
        const code = resp.enrich.code ?? '';
        setErr(
          gracefulFailure(code || null),
        );
        await reload();
        return;
      }
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not suggest');
    } finally {
      setBusy(false);
    }
  };

  /** Card TIL4cDJtZqfW — the same flow with no occasion behind it. The server
   *  owns the reserved 'anytime' key and its "Just because" label, so this is
   *  the same POST with the occasion fields left off; the partner is the
   *  recipient whenever the room has one. */
  const suggestAnytime = () => suggest({
    key: 'anytime', label: 'Just because', date: '', leadDays: 0, forMemberId: partnerId, due: false,
  });

  const accept = async (recId: string) => {
    setErr(null);
    try {
      await api(`/api/gifts/recommendations/${recId}/accept`, { method: 'POST' });
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not accept');
    }
  };

  const resteer = async (recId: string) => {
    setErr(null);
    try {
      await api(`/api/gifts/recommendations/${recId}/resteer`, { method: 'POST', body: { category: 'experience' } });
      setStackIdx((prev) => ({ ...prev, [recId]: 0 }));
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not re-steer');
    }
  };

  /** [Buy] — the deep link opens regardless of analytics; the click event is
   *  fire-and-forget so a blocked request can never stop a navigation. */
  const buy = (recId: string) => {
    void api(`/api/gifts/recommendations/${recId}/click`, { method: 'POST' }).catch(() => {});
  };

  /** [Not this] — advances the stack AND files the title as `not_taste`, the
   *  silent profile write the PRD asks for. Idempotent server-side, so a
   *  double tap cannot stack duplicates. */
  const skip = async (rec: GiftRecommendation, pick: GiftPick) => {
    setErr(null);
    const next = (stackIdx[rec.recId] ?? 0) + 1;
    setStackIdx((prev) => ({ ...prev, [rec.recId]: next }));
    try {
      await api('/api/gifts/private', { method: 'POST', body: { kind: 'not_taste', label: pick.title } });
      setPrivateRows((rows) => (
        rows.some((r) => r.kind === 'not_taste' && r.label.toLowerCase() === pick.title.toLowerCase())
          ? rows
          : [{ rowId: `local-${Date.now()}`, kind: 'not_taste', label: pick.title, createdAt: Date.now() }, ...rows]
      ));
    } catch (e) {
      // The advance already happened, so the stack keeps moving; only say so.
      setErr(e instanceof Error ? e.message : 'Could not remember that');
    }
  };

  /** Card QltAysLlIuwm — the shown-pick tap. One tap files the private
   *  profile row and marks THE PICK answered, so the concierge drops this
   *  title from the NEXT pass. Optimistic: that card's button flips to
   *  "Noted" — never the next card's (MEDIUM-2).
   *
   *  Card ggTQe2KbB0A3 MEDIUM-2 — the failure handler now restores the
   *  stack position too: the advance is optimistic, so a refused write used
   *  to leave the user on a pick whose "Noted" had just rolled back. */
  const pickFeedback = async (rec: GiftRecommendation, verdict: 'has_owned' | 'not_taste' | 'loved', label: string) => {
    setErr(null);
    const key = label.trim().toLowerCase();
    const prevFeedbacks = { ...(rec.pickFeedbacks ?? {}) };
    setRecs((rs) => rs.map((r) => (r.recId === rec.recId
      ? { ...r, pickFeedbacks: { ...(r.pickFeedbacks ?? {}), [key]: verdict } }
      : r)));
    // Card qUTHLEvOs0eR — the two reject chips are also "next": filing the
    // fact moves the stack on when there is another pick to show. "Like it"
    // stays put — liking this one is not a reason to lose it.
    const idx = stackIdx[rec.recId] ?? 0;
    const advanced = verdict !== 'loved' && Boolean(rec.stack[idx + 1]);
    if (advanced) {
      setStackIdx((prev) => ({ ...prev, [rec.recId]: idx + 1 }));
    }
    try {
      const res = await api<{ recommendation?: GiftRecommendation }>(
        `/api/gifts/recommendations/${rec.recId}/pick-feedback`,
        { method: 'POST', body: { verdict, label } },
      );
      // The server's map is the truth now (it holds every pick's answer).
      const fresh = res?.recommendation;
      if (fresh) setRecs((rs) => rs.map((r) => (r.recId === rec.recId ? { ...r, ...fresh } : r)));
    } catch (e) {
      setRecs((rs) => rs.map((r) => (r.recId === rec.recId ? { ...r, pickFeedbacks: prevFeedbacks } : r)));
      // Restore the position the user was looking at before the optimistic
      // advance (MEDIUM-2): the write failed, so the pick is not filed.
      if (advanced) setStackIdx((prev) => ({ ...prev, [rec.recId]: idx }));
      setErr(e instanceof Error ? e.message : 'Could not save that');
    }
  };

  /** Card ggTQe2KbB0A3 MEDIUM-2 — was THIS pick answered? The per-pick map
   *  decides; a legacy row (one verdict, no labels) reads as answered for
   *  whichever pick is showing, which is exactly how it behaved before. */
  const pickAnswered = (r: GiftRecommendation, label: string): boolean => {
    const map = r.pickFeedbacks ?? {};
    const hit = map[label.trim().toLowerCase()];
    if (hit) return true;
    return Object.keys(map).length === 0 && r.pickFeedback !== null;
  };

  /** Card ggTQe2KbB0A3 MEDIUM-5 — APG radiogroup keyboard: arrows move AND
   *  select (selection follows focus for radios), Home/End jump, focus lands
   *  on the chosen button. Shared by the taste pills and the audience pair —
   *  both are `role="radio"` buttons carrying their value on data-value. */
  const radioKeyDown = (
    e: ReactKeyboardEvent<HTMLDivElement>,
    apply: (value: string) => void,
  ) => {
    const btns = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]'));
    if (btns.length === 0) return;
    const from = btns.indexOf(document.activeElement as HTMLButtonElement);
    const checked = btns.findIndex((b) => b.getAttribute('aria-checked') === 'true');
    const cur = from >= 0 ? from : checked;
    let next: number;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (Math.max(cur, 0) + 1) % btns.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (Math.max(cur, 0) - 1 + btns.length) % btns.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = btns.length - 1;
    else return;
    e.preventDefault();
    const value = btns[next]?.dataset.value;
    if (value !== undefined) apply(value);
    btns[next]?.focus();
  };

  const removePrivate = async (rowId: string) => {
    setErr(null);
    const keep = privateRows;
    setPrivateRows((rows) => rows.filter((r) => r.rowId !== rowId));
    try {
      await api(`/api/gifts/private/${rowId}`, { method: 'DELETE' });
    } catch (e) {
      setPrivateRows(keep);
      setErr(e instanceof Error ? e.message : 'Could not remove that');
    }
  };

  const react = async (recId: string, feedback: 'loved' | 'not_quite' | 'not_gifted') => {
    setErr(null);
    try {
      await api(`/api/gifts/recommendations/${recId}/feedback`, { method: 'POST', body: { feedback } });
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save your reaction');
    }
  };

  if (agentOn === false) {
    return (
      <div className="nb-card gifting-agent" data-testid="gifting-agent-off">
        <p className="nb-label">Gifting concierge</p>
        {/* Card cEuitTMwLs6f — this card used to be unreachable AND, before
            that, pointed at Settings and stopped. The permission was the only
            thing off, so the member who has already unlocked the concierge
            turns it on HERE; onOn re-reads the panel in place. */}
        <p className="gifting-muted">One tap turns the concierge on and I'll start suggesting gifts.</p>
        <ConciergeOnButton
          onOn={() => {
            setAgentOn(true);
            void reload();
          }}
          note="or Settings → Concierge"
        />
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="nb-card gifting-agent">
        <p className="nb-label">Gifting concierge</p>
        <p className="gifting-muted">Loading…</p>
      </div>
    );
  }

  const pending = recs.filter((r) => r.status === 'pending');
  const dueReaction = recs.filter((r) => r.feedbackDue && !r.feedback);
  const dueOccasions = occasions.filter((o) => o.due);
  /* Card DqdHN32CEqTg — "On their list": the partner's own saved wishlist,
     shown beside (above) the AI finds instead of blended into them. Gift
     entries AND gift-filed links both count — both are things they flagged
     they want. Rows the partner kept 'just me' never arrive from the API;
     my own rows are excluded here so the lane reads as THEIRS. */
  const onTheirList = partnerId
    ? theirList.filter((i) =>
        i.addedBy === partnerId
        && (i.entryType === 'gift' || i.subtype === 'gifts'))
    : [];
  // Card ZCEkFGirrAOl — the member has not picked a screen yet, so the strip
  // opens where the work is: a pick waiting (or a T+5 due) means Results.
  const active: 'home' | 'brief' | 'results' =
    step ?? (pending.length > 0 || dueReaction.length > 0 ? 'results' : 'home');
  const hasOwned = privateRows.filter((r) => r.kind === 'has_owned').length;
  const notTaste = privateRows.filter((r) => r.kind === 'not_taste').length;
  /* A4 — the recommendation the rail shows: the member's own pick when it is
     still a live one, otherwise the first pending row. */
  const selected = pending.find((r) => r.recId === selRec) ?? pending[0] ?? null;
  /* The split exists only where it means something: the Results screen, with
     something to show, at the token. Any other combination renders the stack
     the way it shipped, and the panel band collapses back with it. */
  const split = wide && active === 'results' && pending.length > 0;

  /* A4 — the detail body. This is the stack card exactly as the phone ships
     it (position head, art, price, detail line, why, order-by, Buy / Save /
     Not this, the per-pick verdict row): on a phone it sits in the list, at
     the token it is the rail's content. Same markup, one source, so the two
     columns can never drift apart. */
  const stackCard = (r: GiftRecommendation) => {
    const idx = stackIdx[r.recId] ?? 0;
    const pick = r.stack[idx];
    if (!pick) return null;
    return (
      <div className="gifting-stack" data-testid={`gift-stack-${r.recId}`}>
        <div className="gifting-stack-head">
          <span className="gifting-stack-pos" data-testid="gift-stack-pos">
            {idx + 1} of {r.stack.length}
          </span>
          {/* Card QR9phRSRQHNx — "Order by" lives on the concise
              card now, not repeated in the head. */}
        </div>
        {/* Card QR9phRSRQHNx + qUTHLEvOs0eR — the standard pick
            card: photo, name, price, detail line, the reason it
            fits, then where to order. Buy is the primary action,
            Save is the secondary one, and the two reject chips
            below are deliberately quiet. */}
        <div className="gifting-stack-card">
          <span className="gifting-for" data-testid="gift-who">
            For {recipientName(r.forMemberId ?? partnerId)}
          </span>
          {/* Card ggTQe2KbB0A3 HIGH-4 — the card says who may see it,
              in the server's words: a 'just me' pick never has to
              guess what the local switch was left on. */}
          <span className="gifting-for gifting-visibility" data-testid="gift-visibility">
            {visibilityName(r)}
          </span>
          {pick.image && <img className="gifting-art" src={pick.image} alt={pick.title} loading="lazy" />}
          <span className="gifting-rec-title" data-testid="gift-pick-name">{pick.title}</span>
          <span className="gifting-rec-meta" data-testid="gift-pick-price">
            {pick.price ?? 'Price not listed'}
            {pick.rating ? ` · ${pick.rating}` : ''}
          </span>
          <span className="gifting-ship" data-testid="gift-pick-desc">
            {[pick.badge, pick.availability].filter(Boolean).join(' · ') || 'Product details from the seller’s listing.'}
          </span>
          {/* Card ggTQe2KbB0A3 MEDIUM-1 — the sentence explains
              THIS pick; stacks stored before per-pick why existed
              fall back to the recommendation-level rationale. */}
          <span className="gifting-rec-rationale" data-testid="gift-pick-why">
            Why it was chosen: {pick.why ?? r.rationale}
          </span>
          {r.orderBy && (
            <span className="gifting-orderby" data-testid="gift-pick-where">
              Order by {r.orderBy}
            </span>
          )}
          {/* Card ZCEkFGirrAOl phase 0 — the actions belong to the
              card, not the gap under it: Buy / Save / Not-this sit
              inside the white card so they clear the fold at 390px. */}
          <div className="gifting-rec-actions">
            <a
              className="pear-btn pear-btn-mustard gifting-buy"
              data-testid="gift-buy"
              href={pick.url}
              target="_blank"
              rel="noreferrer"
              onClick={() => buy(r.recId)}
            >
              Buy
            </a>
            <button
              type="button"
              className="pear-btn gifting-accept gifting-save"
              onClick={() => accept(r.recId)}
            >
              Save
            </button>
            <button
              type="button"
              className="pear-btn gifting-skip"
              data-testid="gift-skip"
              onClick={() => void skip(r, pick)}
            >
              Not this
            </button>
          </div>
          {/* Card QltAysLlIuwm — capture on the shown pick.
              Card ggTQe2KbB0A3 MEDIUM-2: only a pick this
              label's verdict landed on reads "Noted", so
              flicking to the next product keeps its buttons. */}
          <div className="gifting-pick-feedback" data-testid="gift-pick-feedback">
            {pickAnswered(r, pick.title) ? (
              <span className="gifting-pick-noted" data-testid="gift-pick-noted">
                Noted — the concierge will skip this next time.
              </span>
            ) : (
              PICK_VERDICTS.map((v) => (
                <button
                  key={v.value}
                  type="button"
                  className="pear-btn gifting-pick-btn"
                  data-testid={`gift-pick-${v.value}`}
                  onClick={() => void pickFeedback(r, v.value, pick.title)}
                >
                  {v.label}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  /* A4 — the brief row (the list's left column at the token). One button
     carries the whole summary so the row is selectable from the keyboard,
     and the verdict row is deliberately NOT here: the rail owns every action
     on a pick, so the list stays a list. */
  const briefRow = (r: GiftRecommendation, isSel: boolean) => (
    <>
      <button
        type="button"
        className={`gifting-rec-main gifting-brief${isSel ? ' is-on' : ''}`}
        data-testid={`gift-brief-${r.recId}`}
        aria-current={isSel ? 'true' : undefined}
        onClick={() => setSelRec(r.recId)}
      >
        <span className="gifting-for">For {recipientName(r.forMemberId ?? partnerId)}</span>
        <span className="gifting-for gifting-visibility">{visibilityName(r)}</span>
        <span className="gifting-rec-title">{r.giftTitle ?? '—'}</span>
        <span className="gifting-rec-meta">
          {r.occasionLabel} · {r.category}
          {r.orderBy ? ` · order by ${r.orderBy}` : ''}
        </span>
        <span className="gifting-rec-rationale">{r.rationale}</span>
        <span className="gifting-brief-state">{isSel ? 'Detail open' : 'View detail'}</span>
      </button>
      {r.orderBy && <span className="gifting-orderby">Order by {r.orderBy}</span>}
    </>
  );

  return (
    <div className="nb-card gifting-agent" data-testid="gifting-agent">
      {err && <p className="gifting-err">{err}</p>}

      {/* Card ZCEkFGirrAOl — three screens, one strip: Home = the triggers,
          Brief = what you know (positive input only), Results = the picks plus
          the collapsed Fine-Tune footer. All three stay mounted so assistive
          tech and the walks can still read the inactive screens; `hidden`
          takes them out of the rendered flow.

          Card ggTQe2KbB0A3 MEDIUM-5 — the tablist is a real APG tablist now:
          each tab owns its panel (id + aria-controls), the roving tabindex
          follows the selected tab, and Left/Right/Home/End move the selection
          from the keyboard instead of forcing a pointer. */}
      <div
        className="gifting-stepnav"
        role="tablist"
        aria-label="Gifting steps"
        data-testid="gift-steps"
        onKeyDown={(e) => {
          const order = ['home', 'brief', 'results'] as const;
          const i = order.indexOf(active as (typeof order)[number]);
          if (i < 0) return;
          let next: number;
          if (e.key === 'ArrowRight') next = (i + 1) % order.length;
          else if (e.key === 'ArrowLeft') next = (i - 1 + order.length) % order.length;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = order.length - 1;
          else return;
          e.preventDefault();
          const key = order[next];
          setStep(key);
          e.currentTarget.querySelector<HTMLButtonElement>(`[data-step="${key}"]`)?.focus();
        }}
      >
        {([['home', 'Home'], ['brief', 'Brief'], ['results', 'Results']] as const).map(([key, label]) => (
          <button
            key={key}
            id={`gift-tab-${key}`}
            type="button"
            role="tab"
            aria-selected={active === key}
            aria-controls={`gift-panel-${key}`}
            tabIndex={active === key ? 0 : -1}
            data-step={key}
            className={`gifting-stepbtn${active === key ? ' is-on' : ''}`}
            data-testid={`gift-step-${key}`}
            onClick={() => setStep(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── BRIEF (card ZCEkFGirrAOl) — positive profile input only ───── */}
      <section
        className="gifting-recipient"
        data-testid="gift-section-me"
        id="gift-panel-brief"
        role="tabpanel"
        aria-labelledby="gifting-me-heading"
        hidden={active !== 'brief'}
      >
        <h3 className="gifting-recipient-heading" id="gifting-me-heading" data-testid="gift-section-me-heading">
          For you — what would you like?
        </h3>
        <p className="gifting-muted">
          Your own taste. Your partner sees it too, so a gift picked for you lands right.
        </p>

        {/* ── preference (card wYPNXGVzAb63) ────────────────────────── */}
        <p className="nb-label" id="gifting-pref-label">
          Your taste
        </p>
        {/* Card ggTQe2KbB0A3 MEDIUM-5 — arrow keys walk the group. */}
        <div
          className="gifting-prefs"
          role="radiogroup"
          aria-labelledby="gifting-pref-label"
          onKeyDown={(e) => radioKeyDown(e, (v) => {
            const hit = PREFS.find((p) => p.value === v);
            if (hit) void pickPreference(hit.value);
          })}
        >
          {PREFS.map((pv, i) => (
            <button
              key={pv.value}
              type="button"
              role="radio"
              aria-checked={preference === pv.value}
              tabIndex={preference === pv.value || (preference === null && i === 0) ? 0 : -1}
              data-value={pv.value}
              className={`gifting-pill${preference === pv.value ? ' is-on' : ''}`}
              onClick={() => pickPreference(pv.value)}
            >
              {pv.label}
            </button>
          ))}
        </div>

        {/* Card vm9sDf6VWc7S — one audience control, privacy default secret:
            a pick stays off the partner's screen until the searcher widens it.
            Card ggTQe2KbB0A3 HIGH-4: these buttons answer WHO MAY SEE, never
            who the gift is FOR. "For you / For them" read as a recipient
            choice next to a recipient heading, so a person could pick "For
            you" and receive products chosen for someone else — the words are
            visibility-only now, and Results states the recipient and the
            effective visibility off the recommendation the server returned. */}
        <p className="nb-label" id="gifting-audience-label">
          Who can see this search
        </p>
        {/* Card ggTQe2KbB0A3 MEDIUM-5 — arrow keys walk the pair. */}
        <div
          className="gifting-audience"
          role="radiogroup"
          aria-labelledby="gifting-audience-label"
          onKeyDown={(e) => radioKeyDown(e, (v) => {
            if (v === 'secret' || v === 'shared') setAudience(v);
          })}
        >
          <button
            type="button"
            role="radio"
            aria-checked={audience === 'secret'}
            tabIndex={audience === 'secret' ? 0 : -1}
            data-value="secret"
            className={`gifting-audience-btn${audience === 'secret' ? ' is-on' : ''}`}
            data-testid="gift-audience-secret"
            onClick={() => setAudience('secret')}
          >
            Only me
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={audience === 'shared'}
            tabIndex={audience === 'shared' ? 0 : -1}
            data-value="shared"
            className={`gifting-audience-btn${audience === 'shared' ? ' is-on' : ''}`}
            data-testid="gift-audience-shared"
            onClick={() => setAudience('shared')}
          >
            My partner too
          </button>
        </div>
        <p className="gifting-muted" data-testid="gift-audience-hint">
          Only me by default: your partner never sees a pick you have not shared. This chooses who
          can see the search — who it is FOR stays on the occasion you pick below.
        </p>
      </section>

      {/* ── HOME (card ZCEkFGirrAOl) — the trigger bar: occasion, budget,
             fast-ship, and the one full-width way in ─────────────────── */}
      <section
        className="gifting-recipient"
        data-testid="gift-section-them"
        id="gift-panel-home"
        role="tabpanel"
        aria-labelledby="gifting-them-heading"
        hidden={active !== 'home'}
      >
        <h3 className="gifting-recipient-heading" id="gifting-them-heading" data-testid="gift-section-them-heading">
          For them — what would they like?
        </h3>
        <p className="gifting-muted">
          Everything here finds, filters and remembers a gift for your partner.
        </p>

        {/* ── budget + ships-fast (card suGPO1AGmKHj: free-text maximum) ── */}
        <p className="nb-label" id="gifting-budget-label">
          Shopping filters
        </p>
      <div className="gifting-budget">
        <label className="gifting-budget-field" htmlFor="gift-budget-max">
          <span className="gifting-budget-prefix">$</span>
          <input
            id="gift-budget-max"
            type="text"
            inputMode="decimal"
            className="gifting-budget-input"
            placeholder="Any price"
            aria-label="Maximum price"
            aria-invalid={budgetErr !== null}
            data-testid="gift-budget-max"
            value={budgetText}
            onChange={(e) => {
              setBudgetText(e.target.value);
              if (budgetErr) setBudgetErr(null);
            }}
            onBlur={(e) => void saveBudgetMax(e.target.value)}
          />
        </label>
        <button
          type="button"
          aria-pressed={shipsFast}
          className={`gifting-budget-pill gifting-ships${shipsFast ? ' is-on' : ''}`}
          data-testid="gift-ships-fast"
          onClick={() => setShipsFast((v) => !v)}
        >
          Ships fast
        </button>
      </div>
      {budgetErr && <p className="gifting-err" data-testid="gift-budget-error">{budgetErr}</p>}
      <p className="gifting-muted">
        The maximum applies to the shopping pass — items with no listed price stay out of it.
      </p>

      {/* ── anytime entry (card TIL4cDJtZqfW) ────────────────────────── */}
      <p className="nb-label">Find a gift</p>
      {/* Card QR9phRSRQHNx — the wizard opens by outlining its steps, so the
          search never looks like it invented picks from nowhere. */}
      <p className="gifting-lede" data-testid="gift-outline-intro">
        Allow me to help you find an exceptional gift for your partner.
      </p>
      <ol className="gifting-steps" data-testid="gift-outline-steps">
        <li>I read their interests and the private “just for them” list.</li>
        <li>I search live product listings within your budget.</li>
        <li>I bring back a short stack — each one with the reason it fits.</li>
      </ol>
      <p className="gifting-muted">
        No occasion needed — start whenever you like. An upcoming date is just one way in.
      </p>
      <button
        type="button"
          className="pear-btn pear-btn-mustard gifting-anytime"
        data-testid="gift-anytime"
        disabled={busy}
        onClick={() => suggestAnytime()}
      >
        Find a gift
      </button>

      {/* ── occasions (card JKXJy2U9FVwx) ─────────────────────────────── */}
      <p className="nb-label">Upcoming occasions</p>
      {occasions.length === 0 ? (
        <p className="gifting-muted" data-testid="gifting-occasions-empty">
          No upcoming occasions in your settings yet — “Find a gift” above works without one.
        </p>
      ) : (
        <ul className="gifting-occasions">
          {occasions.map((o) => (
            <li key={o.key} className="gifting-occasion">
              {/* Card vC4xl3s56D4n — every row names who it is for */}
              <span className="gifting-for" data-testid="gift-who">
                For {recipientName(o.forMemberId ?? partnerId)}
              </span>
              <div className="gifting-occasion-main">
                <span className="gifting-occasion-label">{o.label}</span>
                <span className="gifting-occasion-date">
                  {o.date}
                  {o.due ? ' · within lead time' : ''}
                </span>
              </div>
              {o.due && (
                <button
                  type="button"
                  className="pear-btn pear-btn-mustard gifting-suggest"
                  disabled={busy}
                  onClick={() => suggest(o)}
                >
                  Suggest
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      </section>

      {/* ── RESULTS (card ZCEkFGirrAOl) — the picks, then the Fine-Tune
             footer: the private lists collapsed behind one disclosure ──── */}
      <section
        className="gifting-recipient"
        data-testid="gift-section-results"
        id="gift-panel-results"
        role="tabpanel"
        aria-labelledby="gifting-results-heading"
        hidden={active !== 'results'}
      >
        <h3 className="gifting-recipient-heading" id="gifting-results-heading" data-testid="gift-section-results-heading">
          Their picks
        </h3>

      {/* ── Desktop uplift A4 (card mPas6koLNVRr): list + detail rail. Below
             the token both wrappers are display:contents and the rail is
             unrendered, so the phone box tree is the one that shipped. ──── */}
      <div className={split ? 'gifting-split is-split' : 'gifting-split'}>
      <div className="gifting-split-list">

      {/* ── Card DqdHN32CEqTg — their own list, above the AI finds.
             Hidden entirely when empty, so a couple with no saved wishes
             sees exactly the strip they saw before this lane existed. ──── */}
      {onTheirList.length > 0 && (
        <div className="gifting-wishlist" data-testid="gift-wishlist">
          <p className="nb-label">On their list</p>
          <p className="gifting-muted">
            Things {recipientName(partnerId)} saved as gift ideas — their own words, before any search.
          </p>
          <ul className="gifting-wishlist-rows">
            {onTheirList.map((w) => (
              <li key={w.itemId} className="gifting-wishlist-item" data-testid="gift-wishlist-item">
                {w.body?.trim() || w.title?.trim() || '—'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── recommendations (Mk8sHH7lfFDM / jqPcHtCfaE7K / Yw5UFURMXSTY) ─ */}
      <p className="nb-label">Recommendations</p>
      {pending.length === 0 ? (
        <p className="gifting-muted" data-testid="gifting-recs-empty">
          No pending recommendations — tap “Find a gift”, or Suggest on a due occasion.
        </p>
      ) : (
        <ul className="gifting-recs">
          {pending.map((r) => {
            const idx = stackIdx[r.recId] ?? 0;
            const pick = r.stack[idx];
            const fallback = (
              <>
                <div className="gifting-rec-main">
                  <span className="gifting-for" data-testid="gift-who">
                    For {recipientName(r.forMemberId ?? partnerId)}
                  </span>
                  {/* Card ggTQe2KbB0A3 HIGH-4 — recipient and visibility are two
                      facts, both read off the returned recommendation. */}
                  <span className="gifting-for gifting-visibility" data-testid="gift-visibility">
                    {visibilityName(r)}
                  </span>
                  <span className="gifting-rec-title">{r.giftTitle ?? '—'}</span>
                  <span className="gifting-rec-meta">
                    {r.occasionLabel} · {r.category}
                    {r.orderBy ? ` · order by ${r.orderBy}` : ''}
                  </span>
                  <span className="gifting-rec-rationale">{r.rationale}</span>
                </div>
                <div className="gifting-rec-actions">
                  <button
                    type="button"
                    className="pear-btn pear-btn-mustard gifting-accept"
                    onClick={() => accept(r.recId)}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    className="pear-btn gifting-steer"
                    onClick={() => resteer(r.recId)}
                  >
                    Re-steer
                  </button>
                </div>
                {/* Card QltAysLlIuwm — capture on the shown pick. Card
                    ggTQe2KbB0A3 MEDIUM-2: the collapse is scoped to this
                    pick's own label, not the recommendation. */}
                <div className="gifting-pick-feedback" data-testid="gift-pick-feedback">
                  {pickAnswered(r, r.giftTitle ?? r.occasionLabel) ? (
                    <span className="gifting-pick-noted" data-testid="gift-pick-noted">
                      Noted — the concierge will skip this next time.
                    </span>
                  ) : (
                    PICK_VERDICTS.map((v) => (
                      <button
                        key={v.value}
                        type="button"
                        className="pear-btn gifting-pick-btn"
                        data-testid={`gift-pick-${v.value}`}
                        onClick={() => void pickFeedback(r, v.value, r.giftTitle ?? r.occasionLabel)}
                      >
                        {v.label}
                      </button>
                    ))
                  )}
                </div>
              </>
            );
            const isSel = selected?.recId === r.recId;
            return (
              <li key={r.recId} className={`gifting-rec${split && isSel ? ' is-selected' : ''}`}>
                {/* A4 — at the token the list carries the BRIEF and the rail
                    carries the pick; below it the stack card stays in the row
                    exactly as it shipped. */}
                {split ? briefRow(r, isSel) : pick ? stackCard(r) : fallback}
                {!pick && !split && r.orderBy && (
                  <span className="gifting-orderby">Order by {r.orderBy}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* ── the T+5 reaction (card Yw5UFURMXSTY) — a gift already given to
             the partner, so it stays inside the FOR THEM section ──────── */}
      {dueReaction.length > 0 && (
        <>
          <p className="nb-label">How did it go?</p>
          <ul className="gifting-feedbacks">
            {dueReaction.map((r) => (
              <li key={r.recId} className="gifting-feedback" data-testid={`gift-feedback-${r.recId}`}>
                <span className="gifting-for" data-testid="gift-who">
                  For {recipientName(r.forMemberId ?? partnerId)}
                </span>
                <span className="gifting-rec-title">{r.giftTitle ?? r.occasionLabel}</span>
                <span className="gifting-rec-meta">{r.occasionLabel}</span>
                <div className="gifting-rec-actions">
                  {REACTIONS.map((rx) => (
                    <button
                      key={rx.value}
                      type="button"
                      className="pear-btn gifting-reaction"
                      data-testid={`gift-reaction-${rx.value}`}
                      onClick={() => void react(r.recId, rx.value)}
                    >
                      {rx.label}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* ── Fine-tune footer (card ZCEkFGirrAOl, epic 4BDpwDu95y0J): the
             negative lists live LAST, collapsed, with counts — never in
             front of a recommendation. The promise and the hint stay
             rendered outside the disclosure so they read at a glance. */}
      <div className="gifting-finetune">
        <p className="nb-label" id="gifting-private-label">
          Fine-tune
        </p>
        <p className="gifting-muted">
          Only you and the concierge read these. Your partner never sees the list.
        </p>
        <p className="gifting-muted" data-testid="gifting-private-hint">
          You don’t add these by hand — tap “Already have it” or “Not their taste”
          on any suggestion above and it lands here.
        </p>
        <details className="gifting-finetune-panel" data-testid="gifting-finetune">
          <summary className="gifting-finetune-summary" data-testid="gift-finetune-summary">
            Already has {hasOwned} · Not their taste {notTaste}
          </summary>
          <div className="gifting-private" data-testid="gifting-private">
            {PRIVATE_KINDS.map((k) => {
              const rows = privateRows.filter((r) => r.kind === k.value);
              return (
                <div className="gifting-private-kind" key={k.value}>
                  <p className="gifting-private-title">
                    {k.label} <span className="gifting-private-count">{rows.length}</span>
                  </p>
                  <p className="gifting-muted">{k.hint}</p>
                  {rows.length > 0 && (
                    <ul className="gifting-private-rows">
                      {rows.map((r) => (
                        <li key={r.rowId} className="gifting-private-row" data-testid={`gift-private-${r.kind}`}>
                          <span>{r.label}</span>
                          <button
                            type="button"
                            className="pear-btn gifting-private-remove"
                            aria-label={`Remove ${r.label}`}
                            onClick={() => removePrivate(r.rowId)}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </details>
      </div>
      </div>

      {/* ── A4 — the rail: the selected recommendation's own detail, parked
             in the notebook context surface (sticky, own scrollport) so the
             list can be read while the pick stays on screen. Esc hands focus
             back to the brief row that opened it. ──────────────────────── */}
      {split && selected && (
        <aside
          className="gifting-split-rail nb-context"
          data-testid="gift-detail-rail"
          aria-label="Recommendation detail"
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              document
                .querySelector<HTMLElement>(`[data-testid="gift-brief-${selected.recId}"]`)
                ?.focus();
            }
          }}
        >
          <p className="nb-label" id="gifting-rail-label">
            Recommendation detail
          </p>
          {stackCard(selected) ?? (
            <p className="gifting-muted" data-testid="gift-rail-empty">
              Still looking — the concierge has not returned a pick for this one yet.
            </p>
          )}
        </aside>
      )}
      </div>
      </section>
    </div>
  );
}
